import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { MarketActionsCell } from 'src/components/market/grid/MarketActionsCell'
import type { Market } from 'src/interfaces/market'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { marketTableHeaderTitles } from './constants'
import { MarketsTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, actions from MarketActionsCell.
 *
 * A factory rather than a module constant: the actions column closes over the page's edit
 * handler. Callers must memoize the result, since a fresh array on every render would reset
 * the grid's column state.
 */
export const buildMarketColumns = (
  onEditMarket: (market: Market) => void,
  // `markets_manage` (ENG-1207 part 2). Edit is the only live row action — Delete is a disabled
  // stub — so without it the actions column is dropped.
  canManage: boolean,
): Array<GridColDef<Market>> => {
  const columns: Array<GridColDef<Market>> = [
    {
      field: MarketsTableFields.title,
      headerName: marketTableHeaderTitles[MarketsTableFields.title],
      flex: 2,
      minWidth: 200,
      sortable: true,
      // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Market>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.title}
          </Typography>
        )
      },
    },
    {
      field: MarketsTableFields.description,
      headerName: marketTableHeaderTitles[MarketsTableFields.description],
      flex: 3,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Market>) => {
        const description = params.row.description?.trim()

        if (!description) {
          // A dash for a missing optional value is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {description}
          </Typography>
        )
      },
    },
    {
      field: MarketsTableFields.createdAt,
      headerName: marketTableHeaderTitles[MarketsTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: MarketsTableFields.actions,
      headerName: marketTableHeaderTitles[MarketsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Market>) => {
        return <MarketActionsCell market={params.row} onEdit={onEditMarket} />
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== MarketsTableFields.actions
  })
}
